import { Profile } from '@garmin/fitsdk'
import type { ProfileField, ProfileMesg, ProfileSubField } from '@garmin/fitsdk'

/**
 * FIT Profile field metadata (units / scale / offset) for the "FIT data"
 * inspector. Presentation-only: nothing here decodes or re-encodes a
 * value -- the decoded activity already holds SDK-scaled values; this only
 * tells the UI what unit a value is in and what its raw on-the-wire
 * integer would have been.
 *
 * Kept behind an async API (`loadFitProfile`) so callers never assume
 * synchronous availability. `@garmin/fitsdk` is already in the main graph
 * via `@fit-editor/fit-core`'s barrel, so it is imported statically (a
 * dynamic `import()` could not code-split it). If main-bundle size
 * matters, the follow-up is to serve this index from the Worker (one
 * Comlink call); the async signature already allows that swap.
 */

export interface FieldMeta {
  /** Field definition number (for a subfield, the parent field's number). */
  num: number
  name: string
  /** Profile type name (`uint16`, `dateTime`, `sport`, ...). */
  type: string
  units: string
  /** The single scale/offset the SDK applies to this field's own value, or `undefined` when it applies none. */
  scale: number | undefined
  offset: number | undefined
  /** True for a subfield (e.g. `garminProduct` on `product`). */
  isSubField: boolean
}

export interface MessageMeta {
  num: number
  /** Profile message name, e.g. `record`, `device_info` is `deviceInfo`. */
  name: string
  messagesKey: string
  /** Lookup by the decoded object key (the field or subfield name). */
  fieldsByName: Map<string, FieldMeta>
  /** Lookup by field definition number, for undocumented numeric keys. */
  fieldsByNum: Map<number, FieldMeta>
}

export interface FitProfileIndex {
  byMessagesKey: Map<string, MessageMeta>
  byNum: Map<number, MessageMeta>
}

/** Collapses a profile scale/offset to the single value the SDK decoder actually applies (mirrors `Decoder#applyScaleAndOffset`). */
function singleValue(value: number | number[]): number | undefined {
  if (Array.isArray(value)) return value.length === 1 ? value[0] : undefined
  return value
}

function singleUnits(units: string | string[]): string {
  if (Array.isArray(units)) return units.length === 1 ? (units[0] ?? '') : ''
  return units
}

function toFieldMeta(
  num: number,
  field: ProfileField | ProfileSubField,
  isSubField: boolean,
): FieldMeta {
  const scale = singleValue(field.scale)
  const offset = singleValue(field.offset)
  return {
    num,
    name: field.name,
    type: field.type,
    units: singleUnits(field.units),
    scale: scale != null && offset != null ? scale : undefined,
    offset: scale != null && offset != null ? offset : undefined,
    isSubField,
  }
}

/** Builds the lookup tables from a `Profile.messages` table. Exported for tests. */
export function buildProfileIndex(messages: Record<number, ProfileMesg>): FitProfileIndex {
  const byMessagesKey = new Map<string, MessageMeta>()
  const byNum = new Map<number, MessageMeta>()
  for (const mesg of Object.values(messages)) {
    const fieldsByName = new Map<string, FieldMeta>()
    const fieldsByNum = new Map<number, FieldMeta>()
    for (const field of Object.values(mesg.fields)) {
      const meta = toFieldMeta(field.num, field, false)
      fieldsByName.set(field.name, meta)
      fieldsByNum.set(field.num, meta)
      for (const sub of field.subFields) {
        if (!fieldsByName.has(sub.name))
          fieldsByName.set(sub.name, toFieldMeta(field.num, sub, true))
      }
    }
    const entry: MessageMeta = {
      num: mesg.num,
      name: mesg.name,
      messagesKey: mesg.messagesKey,
      fieldsByName,
      fieldsByNum,
    }
    byMessagesKey.set(mesg.messagesKey, entry)
    byNum.set(mesg.num, entry)
  }
  return { byMessagesKey, byNum }
}

let profilePromise: Promise<FitProfileIndex> | null = null

/** Lazily loads (once) and indexes the FIT Profile. */
export function loadFitProfile(): Promise<FitProfileIndex> {
  if (profilePromise == null) {
    profilePromise = Promise.resolve().then(() => buildProfileIndex(Profile.messages))
    // Don't cache a failed load forever -- a later mount may retry.
    profilePromise.catch(() => {
      profilePromise = null
    })
  }
  return profilePromise
}

/**
 * The raw on-the-wire integer a scaled numeric value came from, or
 * `undefined` when the field has no non-trivial scale/offset (raw ===
 * value, so showing it again would just be noise). Inverse of the SDK's
 * `value = raw / scale - offset`.
 */
export function rawFromScaled(value: unknown, meta: FieldMeta | undefined): unknown {
  if (meta?.scale == null || meta.offset == null) return undefined
  if (meta.scale === 1 && meta.offset === 0) return undefined
  const { scale, offset } = meta
  const invert = (v: unknown): unknown =>
    typeof v === 'number' && Number.isFinite(v) ? Math.round((v + offset) * scale) : v
  if (typeof value === 'number') return invert(value)
  if (Array.isArray(value) && value.every((v) => typeof v === 'number' || v == null)) {
    return value.map(invert)
  }
  return undefined
}
